import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const Pricing = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Get current user from localStorage
  useEffect(() => {
    const loadUser = () => {
      try {
        const savedUser = localStorage.getItem('user');
        if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
          const userData = JSON.parse(savedUser);
          if (userData && userData.id && userData.id !== -1) {
            setCurrentUser(userData);
          }
        }
      } catch (error) {
        console.error('Error loading user:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadUser();
  }, []);

  const isGuest = !currentUser || currentUser.id === -1;
  const isPremium = currentUser && currentUser.subscription && currentUser.subscription.status === 'active';

  const plans = [
    {
      id: 'trial',
      name: '4-Day Trial',
      price: 'Free',
      originalPrice: null,
      period: '4 Days',
      duration: 4,
      durationType: 'days',
      description: 'Try all premium features for free',
      features: [
        'Full access to Grade 4 & 5 Games',
        'Advanced Math Topics',
        'Progress Analytics',
        'AI Tutoring',
        'All Premium Features'
      ],
      limitations: ['Limited to 4 days only'],
      buttonText: isGuest ? 'Login to Start Trial' : isPremium ? 'Trial Used' : 'Start Free Trial',
      buttonClass: isGuest ? 'bg-blue-600 hover:bg-blue-700' : isPremium ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700',
      popular: false,
      disabled: isPremium,
      priceNrs: 0
    },
    {
      id: 'quarterly',
      name: 'Quarterly Plan',
      price: 'NRs. 4,500',
      originalPrice: 'NRs. 5,400',
      period: '3 Months',
      duration: 3,
      durationType: 'months',
      description: 'Perfect for short-term intensive learning',
      features: [
        'Everything in Trial',
        'All Grade 4 & 5 Games',
        'Advanced Math Topics',
        'Detailed Progress Analytics',
        'Priority Support',
        'Offline Mode Access',
        'Custom Learning Paths'
      ],
      limitations: [],
      buttonText: isGuest ? 'Login to Subscribe' : isPremium ? 'Extend Subscription' : 'Subscribe Now',
      buttonClass: isGuest ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700',
      popular: false,
      priceNrs: 4500,
      savings: '17% OFF'
    },
    {
      id: 'halfyearly',
      name: 'Half-Yearly Plan',
      price: 'NRs. 8,000',
      originalPrice: 'NRs. 10,800',
      period: '6 Months',
      duration: 6,
      durationType: 'months',
      description: 'Most popular choice for consistent learning',
      features: [
        'Everything in Quarterly',
        'Extended Learning Period',
        'Advanced Progress Tracking',
        'Family Sharing (2 accounts)',
        'Priority Customer Support',
        'Early Access to New Games'
      ],
      limitations: [],
      buttonText: isGuest ? 'Login to Subscribe' : isPremium ? 'Extend Subscription' : 'Subscribe Now',
      buttonClass: isGuest ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700',
      popular: true,
      priceNrs: 8000,
      savings: '26% OFF'
    },
    {
      id: 'yearly',
      name: 'Yearly Plan',
      price: 'NRs. 15,000',
      originalPrice: 'NRs. 21,600',
      period: '12 Months',
      duration: 12,
      durationType: 'months',
      description: 'Best value for long-term learning commitment',
      features: [
        'Everything in Half-Yearly',
        'Maximum Savings',
        'Family Sharing (5 accounts)',
        'Dedicated Support Manager',
        'Custom Learning Reports',
        'Beta Access to New Features',
        'Annual Progress Certificate'
      ],
      limitations: [],
      buttonText: isGuest ? 'Login to Subscribe' : isPremium ? 'Extend Subscription' : 'Subscribe Now',
      buttonClass: isGuest ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700',
      popular: false,
      priceNrs: 15000,
      savings: '31% OFF'
    }
  ];

  const handlePlanClick = (plan) => {
    if (isGuest) {
      // Store the selected plan in localStorage and redirect to login
      localStorage.setItem('selectedPlan', JSON.stringify(plan));
      navigate('/login?redirect=pricing');
      return;
    }

    if (plan.id === 'trial' && isPremium) {
      return; // Trial already used
    }

    // Handle subscription logic
    handleSubscription(plan);
  };

  const handleSubscription = async (plan) => {
    // Redirect to payment page with plan data
    navigate('/payment', { 
      state: { 
        plan: {
          id: plan.id,
          name: plan.name,
          price: plan.price,
          priceNrs: plan.priceNrs,
          period: plan.period,
          duration: plan.duration,
          durationType: plan.durationType,
          description: plan.description,
          features: plan.features,
          originalPrice: plan.originalPrice,
          savings: plan.savings
        }
      }
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-8 sm:py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Choose Your Learning Plan
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            Start with our free plan or upgrade to unlock advanced grade-specific games and features
          </p>
        </div>

        {/* User Status Banner */}
        {!isGuest && (
          <div className="mb-8 max-w-2xl mx-auto">
            <div className={`rounded-lg p-4 text-center ${
              isPremium 
                ? 'bg-green-100 border border-green-200 text-green-800' 
                : 'bg-blue-100 border border-blue-200 text-blue-800'
            }`}>
              <p className="font-medium">
                {isPremium 
                  ? `🎉 You have an active premium subscription! Expires: ${currentUser.subscription?.end_date ? new Date(currentUser.subscription.end_date).toLocaleDateString() : 'N/A'}`
                  : '👋 Welcome! Choose a plan to unlock all premium features'
                }
              </p>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-xl p-6 sm:p-8 transition-all hover:scale-105 ${
                plan.popular ? 'ring-2 ring-purple-500 scale-105' : ''
              } ${plan.disabled ? 'opacity-60' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}

              {plan.savings && (
                <div className="absolute -top-2 -right-2">
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    {plan.savings}
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  {plan.originalPrice && (
                    <div className="text-sm text-gray-500 line-through mt-1">{plan.originalPrice}</div>
                  )}
                  <span className="text-gray-600 ml-2">/{plan.period}</span>
                </div>
                <p className="text-gray-600 text-sm">{plan.description}</p>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Features included:</h4>
                <ul className="space-y-2">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {plan.limitations.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Limitations:</h4>
                  <ul className="space-y-2">
                    {plan.limitations.map((limitation, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600 text-sm">{limitation}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-auto">
                <button
                  onClick={() => handlePlanClick(plan)}
                  disabled={plan.disabled}
                  className={`w-full py-3 px-6 rounded-lg text-white font-medium transition-all ${plan.buttonClass} ${
                    plan.disabled ? 'cursor-not-allowed' : 'hover:shadow-lg'
                  }`}
                >
                  {plan.buttonText}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="font-semibold text-gray-900 mb-2">What's included in the 4-day trial?</h3>
              <p className="text-gray-600 text-sm">
                The trial gives you full access to all premium features including Grade 4 & 5 games, 
                advanced AI tutoring, and detailed progress analytics for 4 days completely free.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="font-semibold text-gray-900 mb-2">How do I pay in Nepal?</h3>
              <p className="text-gray-600 text-sm">
                We accept payments through eSewa, Khalti, bank transfers, and mobile banking. 
                All prices are in Nepalese Rupees (NRs.) for your convenience.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="font-semibold text-gray-900 mb-2">Can I cancel anytime?</h3>
              <p className="text-gray-600 text-sm">
                Yes! You can cancel your subscription at any time. You'll continue to have access 
                to premium features until the end of your billing period.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="font-semibold text-gray-900 mb-2">What happens when my subscription expires?</h3>
              <p className="text-gray-600 text-sm">
                When your subscription expires, you'll automatically be downgraded to the free plan. 
                Grade 4 & 5 games will be locked, but you can still access general games.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="font-semibold text-gray-900 mb-2">Which plan offers the best value?</h3>
              <p className="text-gray-600 text-sm">
                The Half-Yearly plan (6 months) offers the best balance of value and commitment, 
                saving you 26% compared to quarterly renewals.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="font-semibold text-gray-900 mb-2">Can I upgrade or extend my plan?</h3>
              <p className="text-gray-600 text-sm">
                Yes! Premium users can extend their subscription or upgrade to a longer duration. 
                The remaining time will be calculated and added to your new plan.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Ready to Start Learning?</h2>
            <p className="text-lg mb-6 opacity-90">
              Join thousands of students already improving their math skills with MathVenture
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-white text-purple-600 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Start Free Trial
              </Link>
              <Link
                to="/games"
                className="border border-white text-white px-8 py-3 rounded-lg font-medium hover:bg-white hover:bg-opacity-10 transition-colors"
              >
                Explore Games
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;